import React from "react";
import PropTypes from "prop-types";
import { Provider as ReduxProvider } from "react-redux";
import { isDesktopApp } from "~/utils/misc";
import { saveCookie } from "~/utils/cookie";

const ContextType = {
  // Enables critical path CSS rendering
  // https://github.com/kriasoft/isomorphic-style-loader
  insertCss: PropTypes.func.isRequired,
  // Integrate Redux
  // http://redux.js.org/docs/basics/UsageWithReact.html
  ...ReduxProvider.childContextTypes,
  // Universal HTTP client
  fetch: PropTypes.func.isRequired,
  pathname: PropTypes.string.isRequired,
  query: PropTypes.object,
};

/**
 * The top-level React component setting context (global) variables
 * that can be accessed from all the child components.
 *
 * https://facebook.github.io/react/docs/context.html
 *
 * Usage example:
 *
 *   const context = {
 *     history: createBrowserHistory(),
 *     store: createStore(),
 *   };
 *
 *   ReactDOM.render(
 *     <App context={context}>
 *       <LayoutComponent>
 *         <LandingPage />
 *       </LayoutComponent>
 *     </App>,
 *     container,
 *   );
 */

class App extends React.PureComponent {
  static propTypes = {
    context: PropTypes.shape(ContextType).isRequired,
    children: PropTypes.element.isRequired
  };

  static childContextTypes = ContextType;

  getChildContext() {
    return this.props.context;
  }

  //-----------------------------------------------------------------------
  // Lifecycle
  //-----------------------------------------------------------------------
  componentDidMount() {
    // Add type of device as class
    if (is.truthy(bowser.mobile)) {
      $("body").addClass("mobile");
    } else if (is.truthy(bowser.tablet)) {
      $("body").addClass("tablet");
    } else {
      $("body").addClass("desktop");
    }

    // Listen for Electron action taps
    if (isDesktopApp() && is.existy(window.electron)) {
      window.electron.onMessageFromRendererToWebview(params => {
        // On purchase clicked
        if (
          is.equal(params.action, "purchase_unlimited_games") ||
          is.equal(params.action, "purchase_restore_purchase")
        ) {
          // Purchase product
          window.electron.purchaseProduct(window.queryParameters.purchase_id);
        } else if (is.equal(params.action, "dark_mode_changed")) {
          this.darkModeChanged(params.value)
        }
      });

      // Listen for purchase events
      emitter.on("purchase_product_initiated", this.purchaseProductInitiated);
      emitter.on(
        "purchase_product_apple_server_error",
        this.purchaseProductAppleServerError
      );
      emitter.on("purchase_product_purchasing", this.purchaseProductPurchasing);
      emitter.on("purchase_product_successful", this.purchaseProductSuccessful);
      emitter.on("purchase_product_error", this.purchaseProductError);
      emitter.on("purchase_product_restored", this.purchaseProductRestored);
    }
  }

  //-----------------------------------------------------------------------
  // Handlers
  //-----------------------------------------------------------------------
  darkModeChanged = (mode) => {
    // Show message
    if (is.equal(mode, "dark")) {
      $("body").attr("data-mode", "dark");
    } else {
      $("body").attr("data-mode", "light");
    }
  };

  purchaseProductInitiated = (type) => {
    // Show message
    if (is.equal(type, "restore")) {
      $.notify("Initiating restore... hang on.");
    } else {
      $.notify("Initiating purchase... hang on.");
    }
  };

  purchaseProductAppleServerError = (type) => {
    // Show message
    if (is.equal(type, "restore")) {
      $.notify("Restore could not start. There's a problem with Apple's servers.");
    } else {
      $.notify("Purchase could not start. There's a problem with Apple's servers.");
    }
  };

  purchaseProductPurchasing = (type) => {
    // Show message
    if (is.equal(type, "restore")) {
      $.notify("Restore started... hang on.");
    } else {
      $.notify("Purchase started... hang on.");
    }
  };

  purchaseProductSuccessful = (type) => {
    // Show message
    $.notify("Purchase was successful... hang on.");

    // Save as purchased
    saveCookie("purchasedUnlimitedGames", true);

    // Reload website
    setTimeout(() => {
      window.location.reload(false);
    }, 3000);
  };

  purchaseProductError = (type) => {
    // Show message
    $.notify("Purchase didn't go through. An error occurred.");
  };

  purchaseProductRestored = (type) => {
    // Show message
    $.notify("Restoring purchase was successful... hang on.");

    // Save as purchased
    saveCookie("purchasedUnlimitedGames", true);

    // Reload website
    setTimeout(() => {
      window.location.reload(false);
    }, 3000);
  };

  //-----------------------------------------------------------------------
  // Render
  //-----------------------------------------------------------------------
  render() {
    // NOTE: If you need to add or modify header, footer etc. of the app,
    // please do that inside the Layout component.
    return React.Children.only(this.props.children);
  }
}

export default App;



// WEBPACK FOOTER //
// ./src/components/App.js